// lib
import React, { useState, useRef, useEffect, useLayoutEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { List, Map, Set } from 'immutable'
import { stopEvent, DOMchildOf } from '@aytm/lib/common/func'
import { verticalScrollIntoContainerView } from '@aytm/lib/common/scroll'

// styles
import style from '@aytm/lib/rfd-ui/styles/basic/inline-select.module.scss'

export default InlineSelect
export { InlineSelectLabel }

const InlineSelectLabelSetOpenedContext = React.createContext(false)

// <InlineSelectOption />
InlineSelectOption.propTypes = {
  option: PropTypes.instanceOf(Map).isRequired,
  selectOptionRef: PropTypes.shape({ current: PropTypes.func.isRequired }).isRequired,
  index: PropTypes.number,
  multiple: PropTypes.bool,
  multipleIconUnchecked: PropTypes.string,
  multipleIconChecked: PropTypes.string,
  disabled: PropTypes.bool,
  optionSelected: PropTypes.bool,
  optionKeyboardSelected: PropTypes.bool
}
function InlineSelectOption({
  option,
  selectOptionRef,
  index,
  multiple = false,
  multipleIconUnchecked = 'icon-aytmfd-afdcheckbox',
  multipleIconChecked = 'icon-aytmfd-afdcheckbox-full',
  disabled = false,
  optionSelected = false,
  optionKeyboardSelected = false,
  optionInvisible = false
} = {}) {
  const optionDisabled = option.has('disabled') && option.get('disabled')()
  const renderOption = option.get('render')
  return (
    <div
      className={`${style.option}${optionDisabled ? ' ' + style['option-disabled'] : ''} ${
        optionInvisible ? ' ' + style['option-invisible'] : ''
      } ${optionSelected ? ' ' + style['option-selected'] : ''}${
        optionKeyboardSelected ? ' ' + style['option-keyboard-selected'] : ''
      }`}
      onMouseDown={
        disabled || optionDisabled
          ? null
          : event => {
              if (1 == event.buttons && !(event.target instanceof HTMLInputElement)) {
                stopEvent(event)
                selectOptionRef.current(option)
              }
            }
      }
      role="menuitem"
      aria-checked={optionSelected}
    >
      {multiple && (
        <i
          className={`${style['option-multiple-icon']} ${
            optionSelected ? `${multipleIconChecked} ${style['option-multiple-icon-selected']}` : multipleIconUnchecked
          }`}
          role="presentation"
        />
      )}
      {'function' === typeof renderOption ? renderOption(index, optionSelected) : renderOption}
    </div>
  )
}
const InlineSelectOptionMemo = React.memo(
  InlineSelectOption,
  (prevProps, nextProps) =>
    prevProps.option === nextProps.option &&
    prevProps.optionSelected === nextProps.optionSelected &&
    prevProps.optionKeyboardSelected === nextProps.optionKeyboardSelected &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.optionInvisible === nextProps.optionInvisible
)

// <InlineSelect />
InlineSelect.propTypes = {
  fetchOptions: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  value: PropTypes.any,
  disabled: PropTypes.bool,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  tabIndex: PropTypes.any,
  multiple: PropTypes.bool,
  focusable: PropTypes.bool,
  multipleIconUnchecked: PropTypes.string,
  multipleIconChecked: PropTypes.string,
  scrollToSelectedOnOpen: PropTypes.bool,
  complex: PropTypes.bool,
  children: PropTypes.any,
  innerRef: PropTypes.any,
  filter: PropTypes.bool,
  filterPlaceholder: PropTypes.string
}
function InlineSelect({
  fetchOptions,
  onSelect,
  onShow,
  onHide,
  value,
  disabled,
  maxHeight,
  width,
  className,
  tabIndex = 0,
  multiple = false,
  focusable = true,
  multipleIconUnchecked = 'icon-aytmfd-afdcheckbox',
  multipleIconChecked = 'icon-aytmfd-afdcheckbox-full',
  scrollToSelectedOnOpen = false,
  children,
  complex,
  innerRef = null,
  filter = false,
  filterPlaceholder = 'filter...'
} = {}) {
  const [options, setOptions] = useState(null)
  const [keyboardIndex, setKeyboardIndex] = useState(-1)
  const [selectedOptions, setSelectedOptions] = useState(Set())
  const inlineSelectLabelSetOpened = useContext(InlineSelectLabelSetOpenedContext)

  const refFocusHolder = useRef()
  const refOptions = useRef()
  const toggledOnMouseDown = useRef(false)
  const hideEvent = useRef()

  const hideOptions = event => {
    if (event && event.relatedTarget instanceof HTMLInputElement && 'innerInput' == event.relatedTarget.dataset?.role)
      return
    if (null != options) {
      setOptions(null)
      if (0 <= keyboardIndex) {
        setKeyboardIndex(-1)
      }
      if (inlineSelectLabelSetOpened) {
        inlineSelectLabelSetOpened(false)
      }
      if (onHide) {
        onHide()
      }
      if (hideEvent.current) {
        document.removeEventListener('click', hideEvent.current, true)
        hideEvent.current = null
      }
    }
  }

  const populateOptions = filterValue => {
    setOptions(List(fetchOptions(filterValue)))
    if (0 <= keyboardIndex) {
      setKeyboardIndex(-1)
    }
  }

  const showOptions = () => {
    populateOptions(null)
    setSelectedOptions(setFromValue(value))
    if (inlineSelectLabelSetOpened) {
      inlineSelectLabelSetOpened(true)
    }
    if (refFocusHolder.current && !complex) {
      refFocusHolder.current.focus({ preventScroll: true })
    }
    if (onShow) {
      onShow()
    }
    if (complex)
      document.addEventListener(
        'click',
        (hideEvent.current = event => {
          if (event.target && !DOMchildOf(event.target, refOptions.current)) {
            hideOptions()
          }
        }),
        true
      )
  }

  useEffect(() => {
    setSelectedOptions(setFromValue(value))
  }, [value])

  useLayoutEffect(() => {
    if (
      scrollToSelectedOnOpen &&
      List.isList(options) &&
      refOptions.current &&
      refOptions.current.scrollHeight > refOptions.current.clientHeight
    ) {
      const firstSelectedOption = refOptions.current.querySelector('.rfd-ui-inline-select-option-selected')
      if (firstSelectedOption) {
        // firstSelectedOption.scrollIntoView(false)
        verticalScrollIntoContainerView(firstSelectedOption, refOptions.current)
      }
    }
  }, [options, scrollToSelectedOnOpen])

  const onClickSelectButton = event => {
    stopEvent(event)
    if (complex) {
      setOptions(List(fetchOptions()))
    }

    if (1 == event.buttons && refFocusHolder.current) {
      toggledOnMouseDown.current = focusable && !complex
      if (null == options) {
        // picking an option closes the list but leaves the holder focused, and focusing it again fires nothing
        if (document.activeElement === refFocusHolder.current) showOptions()
        else refFocusHolder.current.focus({ preventScroll: true })
      } else {
        refFocusHolder.current.blur()
      }
    }
  }

  const selectOptionRef = useRef()
  selectOptionRef.current = multiple
    ? option => {
        const value = option.get('value')
        const updatedSelectedOptions = selectedOptions.has(value)
          ? selectedOptions.delete(value)
          : selectedOptions.add(value)
        setSelectedOptions(updatedSelectedOptions)
        if ('function' == typeof onSelect) {
          onSelect(options.filter(option => updatedSelectedOptions.has(option.get('value'))).toArray())
        }
      }
    : option => {
        if ('function' == typeof onSelect) {
          onSelect(option)
        }
        hideOptions()
      }

  const onKeyDown = event => {
    switch (event.key) {
      case 'ArrowUp': {
        stopEvent(event)
        if (0 <= keyboardIndex) {
          setKeyboardIndex(Math.max(keyboardIndex - 1, -1))
        }
        break
      }
      case 'ArrowDown': {
        stopEvent(event)
        if (List.isList(options)) {
          setKeyboardIndex(Math.min(keyboardIndex + 1, options.size))
        }
        break
      }
      case 'Escape': {
        stopEvent(event)
        if (refFocusHolder.current) {
          hideOptions()
        }
        break
      }
      case 'Enter':
      case ' ': {
        if (filter && ' ' === event.key && null != options) {
          break
        }
        if (List.isList(options) && 0 <= keyboardIndex && options.has(keyboardIndex)) {
          stopEvent(event)
          selectOptionRef.current(options.get(keyboardIndex))
        } else if (null == options) {
          stopEvent(event)
          showOptions()
        }
        break
      }
      case 'Home': {
        stopEvent(event)
        if (0 !== keyboardIndex) {
          setKeyboardIndex(0)
        }
        break
      }
      case 'End': {
        stopEvent(event)
        if (List.isList(options)) {
          setKeyboardIndex(options.size - 1)
        }
        break
      }
      default: {
        break
      }
    }
  }
  useLayoutEffect(() => {
    if (0 <= keyboardIndex && refOptions.current && refOptions.current.scrollHeight > refOptions.current.clientHeight) {
      const keyboardSelectedOptions = refOptions.current.querySelector(`.${style['option-keyboard-selected']}`)
      if (keyboardSelectedOptions) {
        // keyboardSelectedOptions.scrollIntoView(false)
        verticalScrollIntoContainerView(keyboardSelectedOptions, refOptions.current, { margin: 24 })
      }
    }
  }, [keyboardIndex])

  return (
    <div
      ref={innerRef ? innerRef : undefined}
      className={`${style.component}${disabled ? ' ' + style.disabled : ''}${
        null != options ? ' ' + style['component-opened'] : ''
      }${multiple ? ' ' + style['component-multiple'] : ''}${
        0 < selectedOptions.size ? ' ' + style['component-selected'] : ''
      }${null != className ? ' ' + className : ''}`}
      style={{ width }}
      onClick={() => {
        // the mousedown already opened or closed the list; the click that follows must not reopen it
        if (toggledOnMouseDown.current) {
          toggledOnMouseDown.current = false
          return
        }
        showOptions()
      }}
      role="menu"
      aria-haspopup="true"
      aria-expanded={null != options}
    >
      {filter ? null : (
        <div className={style.focusHolder}>
          <input
            type="text"
            onFocus={focusable ? showOptions : null}
            onBlur={hideOptions}
            onKeyDown={onKeyDown}
            ref={refFocusHolder}
            tabIndex={tabIndex}
          />
        </div>
      )}
      <div className={style.select} onMouseDown={onClickSelectButton} style={{ width }}>
        {children}
        <i className={style.arrowDown} style={{ float: null == width ? null : 'right' }} />
      </div>
      {List.isList(options) && (
        <div
          className={`${style.options}${
            0 <= keyboardIndex && keyboardIndex < options.size ? ' ' + style['options-keyboard-selected'] : ''
          }`}
          style={{ maxHeight, width, left: null == width ? null : 0 }}
          ref={refOptions}
        >
          {filter ? (
            <div className={style.filter}>
              <input
                type="text"
                placeholder={filterPlaceholder}
                className={style.filter__input}
                onFocus={showOptions}
                onBlur={hideOptions}
                onKeyDown={onKeyDown}
                ref={refFocusHolder}
                tabIndex={tabIndex}
                autoFocus={true}
                onChange={event => {
                  const filterValue = event.target.value.trim()
                  if (filterValue) {
                    populateOptions(filterValue)
                  } else {
                    populateOptions(null)
                  }
                }}
              />
            </div>
          ) : null}
          {options
            .map((option, index) => (
              <InlineSelectOptionMemo
                option={option}
                selectOptionRef={selectOptionRef}
                index={index}
                disabled={disabled}
                multiple={multiple}
                multipleIconUnchecked={multipleIconUnchecked}
                multipleIconChecked={multipleIconChecked}
                optionSelected={selectedOptions.has(option.get('value'))}
                optionKeyboardSelected={index === keyboardIndex}
                // evaluated in the parent so the memoized row re-renders when the predicate flips (visibility
                // predicates read live refs; inside the memo they were only re-run on unrelated prop changes)
                optionInvisible={Boolean(option.has('invisible') && option.get('invisible')())}
                key={index}
              />
            ))
            .toArray()}
        </div>
      )}
    </div>
  )
}

// <InlineSelectLabel />
InlineSelectLabel.propTypes = {
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  labelRef: PropTypes.any,
  labelStyle: PropTypes.object,
  children: PropTypes.any
}
function InlineSelectLabel({ readOnly, className, labelRef, labelStyle, children } = {}) {
  const [inlineSelectOpened, setInlineSelectOpened] = useState(false)
  return (
    <label
      className={`${style.label}${readOnly ? ' ' + style['label-readOnly'] : ''}${
        inlineSelectOpened ? ' ' + style['label-opened'] : ''
      }${null == className ? '' : ' ' + className}`}
      ref={labelRef}
      style={labelStyle}
    >
      <InlineSelectLabelSetOpenedContext.Provider value={setInlineSelectOpened}>
        {'function' === typeof children ? children(inlineSelectOpened) : children}
      </InlineSelectLabelSetOpenedContext.Provider>
    </label>
  )
}

function setFromValue(value) {
  return void 0 === value
    ? Set()
    : Set().withMutations(set => {
        if (Array.isArray(value)) {
          for (const v of value) {
            set.add(v)
          }
        } else {
          set.add(value)
        }
      })
}
